import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { RegisterRequest } from '@app/api2/api';
import { BPButton } from '@/components/bp/bpbutton';

interface RegisterFormValues {
    email: string;
    password: string;
    confirmPassword: string;
    name: string;
}

export const Register: React.FC = () => {
    const [registerErrors, setRegisterErrors] = useState<string[]>([]);
    
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl') || '/';

    const initialValues: RegisterFormValues = {
        email: '',
        password: '',
        confirmPassword: '',
        name: ''
    };

    const validationSchema: yup.SchemaOf<RegisterFormValues> = yup.object().shape({
        email: yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        name: yup.string()
            .required('Full name is required'),
        password: yup.string()
            .min(6, 'The password must be at least 6 characters long')
            .max(100, 'The password must be at most 100 characters long')
            .required('Password is required'),
        confirmPassword: yup.string()
            .oneOf([yup.ref('password')], 'The password and confirmation password do not match')
            .required('Confirm password is required')
    });

    const handleSubmit = async (values: RegisterFormValues) => {
        setRegisterErrors([]);
        
        try {
            const request: RegisterRequest = {
                email: values.email,
                password: values.password,
                confirmPassword: values.confirmPassword,
                name: values.name
            };

            const result = await authClient.register(request);

            if (result.success) {
                window.location.assign(returnUrl);
            } else {
                setRegisterErrors(result.errors || ['Registration failed. Please try again.']);
            }
        } catch (error) {
            console.error('Registration error:', error);
            setRegisterErrors(['An error occurred during registration. Please try again.']);
        }
    };

    return (
        <AuthLayout>
            <Header as="h1">Register</Header>
            <p>Create a new account.</p>
            
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <Segment>
                    {registerErrors.length > 0 && (
                        <Message error>
                            <Message.Header>Please correct the following errors:</Message.Header>
                            <Message.List items={registerErrors} />
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="name"
                                    label="Full Name"
                                    placeholder="Enter your full name"
                                />

                                <AuthField
                                    name="email"
                                    label="Email"
                                    autoComplete="username"
                                    placeholder="name@example.com"
                                />
                                
                                <AuthField
                                    name="password"
                                    label="Password"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Enter a strong password"
                                />
                                
                                <AuthField
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Confirm your password"
                                />
                                
                                <Divider />
                                
                                <BPButton 
                                    type="submit" 
                                    primary 
                                    size="large" 
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    Register
                                </BPButton>
                            </>
                        )}
                    </BPForm>
                </Segment>
            </div>
            
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
                Already have an account?{' '}
                <a href={`/account/login?returnUrl=${encodeURIComponent(returnUrl)}`}>
                    Log in here
                </a>
            </p>
        </AuthLayout>
    );
};